import { MantineProvider } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { ErrorView } from '../components/ErrorView';
import { theme } from '../styles/mantine';
import { AuthSessionStateProvider } from './AuthSessionState';
import { isChunkLoadError } from '@/lib/telemetry/sentry';
import { FinixEnvironmentProvider } from './FinixEnvironmentContext';
import { DeveloperPanelProvider } from './DeveloperPanelContext';

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [colorScheme] = useLocalStorage<'light' | 'dark'>({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
  });

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => {
        // Cast the error to any to access its properties without TypeScript complaints
        const errorAny = error as any;

        // Check if it's a chunk loading error
        if (isChunkLoadError(errorAny)) {
          // Automatically refresh after showing the message
          setTimeout(() => window.location.reload(), 1500);

          // Show a friendly "updating" message
          return (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
              }}
            >
              <h2>Refreshing with updates!</h2>
              <p>We&apos;ve made some improvements to the app. Reloading...</p>
            </div>
          );
        }

        // For other errors, use the default ErrorView
        return <ErrorView />;
      }}
    >
      <MantineProvider defaultColorScheme={colorScheme} theme={theme}>
        <AuthSessionStateProvider>
          <Notifications position='bottom-left' />
          <FinixEnvironmentProvider>
            <DeveloperPanelProvider>{children}</DeveloperPanelProvider>
          </FinixEnvironmentProvider>
        </AuthSessionStateProvider>
      </MantineProvider>
    </Sentry.ErrorBoundary>
  );
};

export default AppProvider;
